import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, Observable, startWith, map } from 'rxjs';

import { Site } from '../../objects/site';
import { SiteService } from '../../service/entity-services/site.service';
import { CreateTypeContract } from '../create-type-contract';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
  selector: 'app-new-site',
  templateUrl: './new-site.component.html',
  styleUrls: ['./new-site.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatAutocompleteModule
  ]
})
export class NewSiteComponent extends CreateTypeContract implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);

  // Form and state management
  form: FormGroup;
  private formSubscription!: Subscription;

  // State autocomplete
  states: string[] = [];
  filteredStates$!: Observable<string[]>;
  
  currentSite: Site = {};
  
  // UI state
  saving = false;
  editingSiteMode = false;
  savedAsInactiveInDb = false;
  okToLeaveIntakeForm = false;
  
  // Validation
  submitted = false;

  constructor() {
    super();
    
    // Generate site number with timestamp
    const datetimestamp = (new Date()).getTime().toString();
    
    // Initialize reactive form
    this.form = this.fb.group({
      siteNum: [{ value: datetimestamp, disabled: true }],
      name: ['', [Validators.required, Validators.minLength(2)]],
      address: [''],
      city: [''],
      state: [''],
      zip: ['', [Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      businessUnitNum: [''],
      district: [''],
      division: [''],
      county: [''],
      fka: [''], // Formerly Known As
      squareFootage: ['', [Validators.pattern(/^\d+$/)]],
      program: [''],
      inactive: [false],
      comments: ['']
    });
  }

  async ngOnInit() {
    // Check if we're in edit mode (passed via router state)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;

    if (state?.site && state?.editMode) {
      this.editingSiteMode = true;
      this.currentSite = state.site;
      this.populateFormWithSite(state.site);
    }

    // Subscribe to form value changes
    this.formSubscription = this.form.valueChanges.subscribe(changes => {
      // Update currentSite with form values (including disabled fields)
      this.currentSite = {
        ...this.currentSite,
        ...changes,
        siteNum: this.form.get('siteNum')?.value // Include disabled field
      };
    });

    // Load states for autocomplete
    this.commonSheetsService.getGeographicStates().subscribe(states => {
      this.states = states || [];
      this.setupStateAutocomplete();
    });
  }

  /**
   * Populate form fields with existing site data for editing
   */
  private populateFormWithSite(site: Site) {
    this.form.patchValue({
      name: site.name || '',
      address: site.address || '',
      city: site.city || '',
      state: site.state || '',
      zip: site.zip || '',
      businessUnitNum: site.businessUnitNum || '',
      district: site.district || '',
      division: site.division || '',
      county: site.county || '',
      fka: site.fka || '',
      squareFootage: site.squareFootage || '',
      program: site.program || '',
      inactive: site.inactive || false,
      comments: site.comments || ''
    });

    // Update the siteNum (disabled field) to use existing value
    this.form.get('siteNum')?.setValue(site.siteNum);
  }

  private setupStateAutocomplete() {
    const stateControl = this.form.get('state');
    if (stateControl) {
      this.filteredStates$ = stateControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterStates(value || ''))
      );
    }
  }

  private filterStates(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.states.filter(state =>
      state.toLowerCase().includes(filterValue)
    );
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  // Form validation methods
  isFormValid(): boolean {
    return this.form.valid && !this.saving;
  }

  getValidationMessage(): string {
    if (this.form.get('name')?.hasError('required')) {
      return 'Site name is required';
    }
    if (this.form.get('name')?.hasError('minlength')) {
      return 'Site name must be at least 2 characters';
    }
    if (this.form.get('zip')?.hasError('pattern')) {
      return 'Invalid ZIP code format (use 12345 or 12345-6789)';
    }
    if (this.form.get('squareFootage')?.hasError('pattern')) {
      return 'Square footage must be a number';
    }
    return 'Please correct the errors above';
  }

  // Event handlers
  inactiveToggleChanged(event: any) {
    const isInactive = event.checked;
    this.form.patchValue({ inactive: isInactive });
  }

  cancelSubmit() {
    this.okToLeaveIntakeForm = true;
    this.router.navigateByUrl('/securehome/sites');
  }


  
  async onSubmit() {
    this.submitted = true;
    
    if (!this.isFormValid()) {
      return;
    }

    this.saving = true;
    this.okToLeaveIntakeForm = true;

    try {
      // Get form value including disabled fields
      const siteToSave: Site = {
        ...this.form.getRawValue() // getRawValue includes disabled fields
      };

      console.log('SITE TO SAVE: ', siteToSave);

      // Call the service and wait for it to complete
      await this.siteService.writeSite(siteToSave).toPromise();

      console.log('Site saved successfully');
      this.router.navigateByUrl('/securehome/sites');

    } catch (error) {
      console.error('Error saving site:', error);
      // Show error message to user
      alert('Failed to save site. Please try again.');
      this.saving = false; // Re-enable the save button on error
    } finally {
      // Only set saving to false if navigation didn't happen
      // (if save succeeded, component will be destroyed anyway)
    }
  }

  // Helper methods for display
  get isEditMode(): boolean {
    return this.editingSiteMode;
  }

  get canSave(): boolean {
    return !(this.savedAsInactiveInDb && this.currentSite.inactive);
  }
}